import CheckoutForm from '@/features/checkout/components/CheckoutForm';
import { useToast } from '@/hooks/useToast';
import { API_BASE_URL, api } from '@/lib/api';
import { formatCurrency, formatEventDate, formatSeatIdentifier } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import type { Event, Seat } from '@eventflow/shared-types';
import type { CheckoutFormData } from '@eventflow/validation';
import { Info } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const [event, setEvent] = useState<Event | null>(null);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Recuperar datos del localStorage (solo una vez)
  const selectedSeatIds = useMemo(
    () => JSON.parse(localStorage.getItem('selectedSeatIds') || '[]'),
    []
  );
  const eventId = useMemo(() => localStorage.getItem('eventId') || '', []);
  const sessionIdRef = useRef<string>(localStorage.getItem('eventflow_session_id') || nanoid());

  // Ref para prevenir liberación en Strict Mode (React 18+)
  const isMountedRef = useRef(false);

  useEffect(() => {
    // Validar que haya asientos seleccionados
    if (selectedSeatIds.length === 0 || !eventId) {
      navigate('/eventos');
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Fetch event
        const eventData = await api.get<Event>(`/events/${eventId}`);
        setEvent(eventData);

        // Fetch all seats for this event
        const seatsData = await api.get<Seat[]>(`/events/${eventId}/seats`);

        // Filtrar solo los asientos seleccionados
        const selectedSeats = seatsData.filter((seat) => selectedSeatIds.includes(seat.id));
        setSeats(selectedSeats);

        // Verificar que los asientos sigan bloqueados por esta sesión
        const invalidSeats = selectedSeats.filter(
          (seat) => seat.status !== 'locked' || seat.locked_by_session !== sessionIdRef.current
        );

        if (invalidSeats.length > 0) {
          const errorMsg =
            'Algunos asientos ya no están disponibles o tu sesión expiró. Regresa y selecciona nuevamente.';
          setError(errorMsg);
          showError(errorMsg);
        } else {
          setError('');
        }
      } catch (err) {
        console.error('[Checkout] Error fetching checkout data:', err);
        const errorMsg = 'Error al cargar los datos del checkout. Intenta nuevamente.';
        setError(errorMsg);
        showError(errorMsg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, navigate, selectedSeatIds]);

  // Liberar asientos si el usuario sale de checkout sin completar
  useEffect(() => {
    // Dar tiempo a React Strict Mode para completar el ciclo de mount/unmount
    const timer = setTimeout(() => {
      isMountedRef.current = true;
    }, 100);

    const handleBeforeUnload = () => {
      const storedSeats = localStorage.getItem('selectedSeatIds');
      if (storedSeats && storedSeats !== '[]') {
        navigator.sendBeacon(
          `${API_BASE_URL}/seats/release`,
          JSON.stringify({
            seatIds: JSON.parse(storedSeats),
            sessionId: sessionIdRef.current,
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // SOLO liberar si el componente estuvo montado el tiempo suficiente
      if (isMountedRef.current) {
        const storedSeats = localStorage.getItem('selectedSeatIds');
        if (storedSeats && storedSeats !== '[]') {
          api
            .post('/seats/release', {
              seatIds: JSON.parse(storedSeats),
              sessionId: sessionIdRef.current,
            })
            .catch(() => {
              /* silent */
            });
        }
      }
    };
  }, []);

  const handleCheckoutSubmit = async (formData: CheckoutFormData) => {
    if (!event) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await api.post<{
        purchase: { id: string };
        accountCreated?: {
          email: string;
          password: string;
          message: string;
        };
      }>('/checkout/create', {
        eventId: event.id,
        seatIds: selectedSeatIds,
        sessionId: sessionIdRef.current,
        ...formData,
      });

      // Limpiar localStorage ANTES de navegar
      // Esto previene que el cleanup libere los asientos
      localStorage.removeItem('selectedSeatIds');
      localStorage.removeItem('eventId');

      // Redirigir a la página de confirmación con información de cuenta creada
      navigate(`/compra/${response.purchase.id}`, {
        state: response.accountCreated
          ? {
              accountCreated: response.accountCreated,
            }
          : undefined,
      });
    } catch (err) {
      console.error('[Checkout] Error creating purchase:', err);
      const errorMsg =
        err instanceof Error ? err.message : 'Error al procesar la compra. Intenta nuevamente.';
      setError(errorMsg);
      showError(errorMsg);
      setIsSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return seats.reduce((sum, seat) => sum + seat.price, 0);
  };

  // Array de asientos ordenados (no usado actualmente)

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando checkout...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/eventos')} className="btn-primary">
            Volver a Eventos
          </button>
        </div>
      </div>
    );
  }

  if (!event || seats.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No hay asientos para procesar</p>
          <button onClick={() => navigate('/eventos')} className="btn-primary">
            Volver a Eventos
          </button>
        </div>
      </div>
    );
  }

  const total = calculateTotal();

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <button
          onClick={() => navigate(`/eventos/${eventId}`)}
          className="text-sm text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 flex items-center"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Volver a selección
        </button>

        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
          Finalizar Compra
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Completa tus datos para confirmar la reserva
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Formulario (2 columnas) */}
        <div className="lg:col-span-2">
          {error && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center">
                <svg
                  className="h-5 w-5 text-red-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <CheckoutForm
            onSubmit={handleCheckoutSubmit}
            isLoading={isSubmitting}
            isAuthenticated={isAuthenticated}
            defaultEmail={user?.email || ''}
          />
        </div>

        {/* Resumen de Compra (1 columna) */}
        <div className="lg:col-span-1 order-first lg:order-last">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4 sm:p-6 lg:sticky lg:top-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Resumen de Compra
            </h2>

            {/* Evento */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1">{event.name}</h3>
              <p className="text-sm text-gray-600">
                {formatEventDate(event.event_date, { shortFormat: true })}
              </p>
              <p className="text-sm text-gray-600">{event.venue_name}</p>
            </div>

            {/* Asientos */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Asientos Seleccionados</h4>
              <div className="space-y-2">
                {seats.map((seat) => (
                  <div key={seat.id} className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">
                      {formatSeatIdentifier(seat.seat_identifier)}
                    </span>
                    <span className="font-medium text-gray-900">{formatCurrency(seat.price)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Subtotal</span>
                <span className="font-medium text-gray-900">{formatCurrency(total)}</span>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-blue-600">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-50 rounded-lg p-4 text-sm text-gray-700">
              <p className="font-medium mb-2 flex items-center gap-1">
                <Info className="w-4 h-4" />
                Información importante:
              </p>
              <ul className="space-y-1 text-xs">
                <li>• Tus asientos están reservados por 10 minutos</li>
                <li>• El pago se realiza por transferencia bancaria</li>
                <li>• Recibirás instrucciones después de confirmar</li>
                <li>• Los tickets se envían por email al confirmar el pago</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
